import _ from 'lodash';
import { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArchivalAidMetadata,
  ArchivalCollection,
  EadNode,
  EadNodeParentView,
} from 'types';
import { getNodeId } from 'utils/ead-utils';
import { useEadDetail } from '../hooks/ead-detail-hook';
import { useArchivalUnitDetail } from '../hooks/archival-unit-detail-hook';
import { useArchivalAidDetail } from '../hooks/aid-detail-hook';
import { useVariantContext } from 'components/grid/v2/context/variant-context';
import { useArchivalCollectionDetail } from '../hooks/archival-collection-hook';
import { GridRootUrl, HierarchyContext } from './hierarchy-context';

export type Breadcrumb = {
  node: EadNode | EadNodeParentView | ArchivalAidMetadata | ArchivalCollection;
  to: string;
  level: string;
  timeRange?: string;
  levelLabel: string;
  label: string;
};
export function createEadBreadcrumbs(
  ead: EadNode,
  root: GridRootUrl,
  collectionDetail?: ArchivalCollection
): Breadcrumb[] {
  const stack: EadNodeParentView[] = [];
  let node: EadNodeParentView = (ead as unknown) as EadNodeParentView;
  while (node.parent) {
    stack.push({ ...node });
    node = { ...node.parent };
  }
  stack.push({ ...node });

  if (root === GridRootUrl.COLLECTIONS) {
    const rootCollectionId = node?.archivalAidMetadata?.archivalCollection?.id;
    const breadcrumbs: Breadcrumb[] = stack.map((s, index) => {
      const isLast = index === stack.length - 1;
      const to = isLast
        ? `${root}/${rootCollectionId}/ead/${getNodeId(node)}`
        : `${root}/${rootCollectionId}/ead/${getNodeId(s)}`;
      return {
        node: s,
        to,
        level: isLast ? 'aid' : s.level,
        levelLabel: isLast ? 'Pomůcka' : s.levelLabel,
        timeRange: _.get(s, 'timeRange', '') as string,
        label:
          _.get(s, 'label', undefined) ??
          _.get(s, 'title', undefined) ??
          _.get(s, 'name', undefined) ??
          _.get(s, 'archivalAidMetadata.name', undefined) ??
          '',
      };
    });
    breadcrumbs.push({
      node: collectionDetail ?? node,
      to: `${root}/${rootCollectionId}`,
      level: 'funds',
      levelLabel: 'Fond',
      timeRange: _.get(collectionDetail, 'timeRange', '') as string,
      label:
        _.get(collectionDetail, 'name', undefined) ??
        _.get(collectionDetail, 'label', undefined) ??
        '??',
    });
    return breadcrumbs.reverse();
  }

  const rootAidId = node.archivalAidMetadata?.id;
  const breadcrumbs: Breadcrumb[] = stack.map((s, index) => ({
    node: s,
    to: `${root}/${rootAidId}/ead/${getNodeId(s)}`,
    level: index === stack.length - 1 ? 'aid' : s.level,
    levelLabel: index === stack.length - 1 ? 'Pomůcka' : s.levelLabel,
    timeRange: _.get(s, 'timeRange', '') as string,
    label:
      _.get(s, 'label', undefined) ??
      _.get(s, 'title', undefined) ??
      _.get(s, 'name', undefined) ??
      _.get(s, 'archivalAidMetadata.name', undefined) ??
      '',
  }));

  return breadcrumbs.reverse();
}

export const BreadcrumbsContext = createContext<Breadcrumb[]>([]);

const useFundsVariantBreadcrumbs = () => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  const {
    archivalAidId,
    eadId,
    archivalCollectionId,
    archivalUnitId,
  } = useContext(HierarchyContext);
  const { data: collection } = useArchivalCollectionDetail(
    archivalCollectionId
  );
  const { data: aid } = useArchivalAidDetail(archivalAidId);
  const { data: ead } = useEadDetail(eadId);
  const { data: archivalUnit } = useArchivalUnitDetail(archivalUnitId);

  useEffect(() => {
    if (!archivalCollectionId) {
      setBreadcrumbs([]);
    } else if (eadId && ead && collection) {
      const crumbs = createEadBreadcrumbs(
        ead,
        GridRootUrl.COLLECTIONS,
        collection
      );
      setBreadcrumbs(crumbs);
    } else if (archivalCollectionId && collection) {
      const crumbs = [
        {
          node: collection,
          to: `${GridRootUrl.COLLECTIONS}/${archivalCollectionId}`,
          level: 'funds',
          levelLabel: 'Fond',
          timeRange: '',
          label: collection?.name ?? collection?.title ?? '',
        },
      ];
      if (aid) {
        crumbs.push({
          node: aid,
          to: `${GridRootUrl.COLLECTIONS}/${archivalCollectionId}/pomucka/${archivalAidId}`,
          level: 'aid',
          levelLabel: 'Pomůcka',
          timeRange: aid?.timeRange ?? '',
          label: aid?.name ?? aid?.title ?? '',
        });
      }
      if (archivalUnit && archivalUnitId) {
        crumbs.push({
          node: archivalUnit,
          to: `${GridRootUrl.COLLECTIONS}/${archivalCollectionId}/archivalie/${archivalUnitId}`,
          level: 'archival',
          levelLabel: 'Archivalie',
          timeRange: archivalUnit?.timeRange ?? '',
          label: archivalUnit?.summary ?? archivalUnit?.label ?? '',
        });
      }
      setBreadcrumbs([...crumbs]);
    }
  }, [
    aid,
    archivalAidId,
    collection,
    archivalCollectionId,
    eadId,
    ead,
    archivalUnit,
    archivalUnitId,
  ]);
  return {
    breadcrumbs,
  };
};

const useAidsVariantBreadcrumbs = () => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const { id, eadId, archivalUnitId } = useParams<{
    eadId: string;
    archivalUnitId: string;
    id: string;
    archivalAidId: string;
  }>();
  const aidResponse = useArchivalAidDetail(id);
  const eadResponse = useEadDetail(eadId);
  const auResponse = useArchivalUnitDetail(archivalUnitId);

  useEffect(() => {
    if (!id) {
      setBreadcrumbs([]);
    } else if (eadId && eadResponse.data) {
      setBreadcrumbs(createEadBreadcrumbs(eadResponse.data, GridRootUrl.AIDS));
    } else if (id && archivalUnitId && auResponse.data && aidResponse.data) {
      setBreadcrumbs([
        {
          node: aidResponse.data,
          label: aidResponse.data.name ?? aidResponse.data.title ?? '',
          level: 'aid',
          levelLabel: 'Pomůcka',
          to: `${GridRootUrl.AIDS}/${id}`,
          timeRange: '',
        },
        {
          node: auResponse.data,
          label: auResponse.data.summary ?? auResponse.data.label ?? '',
          level: 'item',
          levelLabel: 'archiválie',
          to: `${GridRootUrl.AIDS}/${id}/archivalie/${archivalUnitId}`,
          timeRange: '',
        },
      ]);
    } else if (id && aidResponse.data) {
      setBreadcrumbs([
        {
          node: aidResponse.data,
          label: aidResponse.data.name ?? aidResponse.data.title ?? '',
          level: 'aid',
          levelLabel: 'Pomůcka',
          to: `${GridRootUrl.AIDS}/${id}`,
          timeRange: '',
        },
      ]);
    }
  }, [
    auResponse.data,
    archivalUnitId,
    aidResponse.data,
    id,
    eadId,
    eadResponse.data,
  ]);

  return { breadcrumbs };
};

export const useBreadcrumbs = () => {
  const variant = useVariantContext();
  return variant === 'funds'
    ? useFundsVariantBreadcrumbs
    : useAidsVariantBreadcrumbs;
};
